import React, { useState, useEffect } from 'react';
import BackgroundIcons from '../../components/BackgroundIcons';

const MemoryCapsuleCreator = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const pageIcons = [
    { emoji: '📸', size: '5xl', opacity: '15' },
    { emoji: '💕', size: '3xl', opacity: '15' },
    { emoji: '🎁', size: '6xl', opacity: '15' },
    { emoji: '💖', size: '4xl', opacity: '15' },
    { emoji: '🕰️', size: '5xl', opacity: '15' },
    { emoji: '💌', size: '6xl', opacity: '15' },
    { emoji: '✨', size: '5xl', opacity: '15' }
  ];

  const memoryCategories = [
    { 
      id: 'moments', 
      title: 'Special Moments', 
      icon: '✨', 
      description: 'Capture the magical moments that made your day special'
    },
    { 
      id: 'hopes', 
      title: 'Hopes & Dreams', 
      icon: '🌟', 
      description: 'Your aspirations and dreams as newlyweds'
    },
    { 
      id: 'predictions', 
      title: 'Future Predictions', 
      icon: '🔮', 
      description: 'Fun predictions about your future selves'
    },
    { 
      id: 'advice', 
      title: 'Advice to Future Selves', 
      icon: '💝', 
      description: 'Wisdom and advice for your future married life'
    }
  ];

  const features = [
    { icon: '📸', title: 'Photo Gallery', description: 'Upload your favorite wedding photos' },
    { icon: '🤖', title: 'AI Story Generation', description: 'AI creates a beautiful narrative from your memories' },
    { icon: '🔒', title: 'Time-Locked', description: 'Sealed until your chosen anniversary date' },
    { icon: '💌', title: 'Email Reminders', description: 'Get notified when it\'s time to open' },
    { icon: '📥', title: 'PDF Download', description: 'Download your complete memory book' },
    { icon: '✨', title: 'Beautiful Design', description: 'Premium VowAI styling throughout' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={pageIcons} />
      
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(25)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-15"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${4 + Math.random() * 3}s`
            }}
          >
            <span className="text-gold-300 text-sm">✨</span>
          </div>
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 relative z-10">
        
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-2xl">📦</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Memory Capsule Creator
          </h1>
          
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400">💖</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Create a digital time capsule filled with your wedding memories, hopes, and dreams to open on a future anniversary
          </p>
        </div>

        {/* Coming Soon Overlay */}
        <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          {/* Blurred Preview Content */}
          <div className="filter blur-sm pointer-events-none select-none">
            <div className="grid lg:grid-cols-3 gap-8 mb-8">
              {/* Preview Cards */}
              {memoryCategories.map((category, index) => (
                <div key={category.id} className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                      <span className="text-2xl">{category.icon}</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                        {category.title}
                      </h3>
                      <p className="text-sm text-gray-600 font-light">
                        {category.description}
                      </p>
                    </div>
                  </div>
                  <div className="h-32 bg-gray-100 rounded-xl"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Coming Soon Banner */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border-4 border-gold-300 p-12 max-w-2xl mx-4 transform hover:scale-105 transition-all duration-300">
              
              {/* Animated Icon */}
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <div className="w-24 h-24 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full flex items-center justify-center shadow-2xl animate-pulse">
                    <span className="text-5xl">🚀</span>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gold-400 rounded-full flex items-center justify-center animate-bounce">
                    <span className="text-sm">✨</span>
                  </div>
                </div>
              </div>

              {/* Coming Soon Text */}
              <h2 className="text-5xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-4 text-center">
                Coming Soon!
              </h2>
              
              <p className="text-xl text-gray-700 text-center mb-8 font-light leading-relaxed">
                We're crafting something magical for you. The Memory Capsule Creator will let you seal your wedding memories in a digital time capsule to open on future anniversaries.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl">
                    <span className="text-2xl flex-shrink-0">{feature.icon}</span>
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm">{feature.title}</h4>
                      <p className="text-xs text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* VowAI Branding */}
              <div className="text-center pt-6 border-t-2 border-gold-200">
                <p className="text-sm text-gray-600 font-light italic">
                  Part of your VowAI wedding journey 💕
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`mt-16 text-center transition-all duration-700 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-gold-400 to-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-light italic">
            Treasuring every memory of your VowAI wedding journey
          </p>
        </div>
      </div>
    </div>
  );
};

export default MemoryCapsuleCreator;
